import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, ArrowLeft, FlaskConical, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/App';
import { BancardFrame } from '@/components/bancard/BancardFrame';
import { BancardResult, formatGs } from '@/components/bancard/BancardResult';

const POLL_MS = 3000;
const MAX_POLLS = 12;

export default function BancardCheckoutPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const requestId = searchParams.get('request_id');
  const processId = searchParams.get('process_id');
  const env = searchParams.get('env') || 'staging';
  const amount = searchParams.get('amount');
  const bancardStatus = searchParams.get('status'); // payment_success | payment_fail (added by Bancard on return)
  const cancelledParam = searchParams.get('cancelled');

  const [phase, setPhase] = useState('form'); // form | verifying | result
  const [variant, setVariant] = useState('pending');
  const [status, setStatus] = useState(null);
  const [errorText, setErrorText] = useState('');
  const [frameLoading, setFrameLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef({ attempts: 0, timer: null, active: false });
  const statusRef = useRef(null);

  const stopPolling = () => {
    pollRef.current.active = false;
    clearTimeout(pollRef.current.timer);
  };

  const showResult = useCallback((v, s, text = '') => {
    stopPolling();
    setStatus(s || null);
    setVariant(v);
    setErrorText(text);
    setPhase('result');
  }, []);

  const verify = useCallback(() => {
    if (!requestId) return;
    stopPolling();
    pollRef.current = { attempts: 0, timer: null, active: true };
    setPhase('verifying');
    const tick = async () => {
      if (!pollRef.current.active) return;
      pollRef.current.attempts += 1;
      try {
        const res = await api.get(`/payments/bancard/status/${requestId}?sync=true`);
        const data = res.data;
        statusRef.current = data;
        if (data.payment_status === 'paid' || data.request_status === 'paid') return showResult('success', data);
        if (data.payment_status === 'failed') return showResult('failed', data);
        if (data.payment_status === 'rolled_back') return showResult('cancelled', data);
        setStatus(data);
      } catch (e) {
        if (e.response?.status === 401) return showResult('error', null, 'Tu sesión expiró. Iniciá sesión para ver el estado de tu pago.');
      }
      if (!pollRef.current.active) return;
      if (pollRef.current.attempts >= MAX_POLLS) return showResult('pending', statusRef.current);
      pollRef.current.timer = setTimeout(tick, POLL_MS);
    };
    tick();
  }, [requestId, showResult]);

  // Entry: cancelled by Bancard's cancel_url, returned via return_url (status param), or fresh form
  useEffect(() => {
    if (!requestId) {
      showResult('error', null, 'Datos de pago incompletos. Volvé a la propiedad e intentá de nuevo.');
    } else if (cancelledParam) {
      api.post(`/booking-requests/${requestId}/bancard/cancel`).catch(() => {})
        .finally(() => api.get(`/payments/bancard/status/${requestId}`).then((r) => showResult('cancelled', r.data)).catch(() => showResult('cancelled', null)));
    } else if (bancardStatus || !processId) {
      verify();
    } else {
      setPhase('form');
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, processId, bancardStatus, cancelledParam]);

  const handleFrameResult = useCallback((message, details) => {
    if (message === 'payment_fail' && details) toast.error(details);
    verify();
  }, [verify]);

  // While the form is open, quietly watch for a confirmation that arrived server-side (webhook)
  useEffect(() => {
    if (phase !== 'form' || !requestId) return undefined;
    const id = setInterval(async () => {
      try {
        const res = await api.get(`/payments/bancard/status/${requestId}`);
        if (res.data.payment_status === 'paid') showResult('success', res.data);
        else if (res.data.payment_status === 'failed') showResult('failed', res.data);
      } catch (_) { /* silent */ }
    }, 10000);
    return () => clearInterval(id);
  }, [phase, requestId, showResult]);

  const handleRetry = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/booking-requests/${requestId}/checkout-bancard`);
      const { process_id, bancard_env, amount: newAmount } = res.data;
      setFrameLoading(true);
      setStatus(null);
      setSearchParams({ request_id: requestId, process_id, env: bancard_env, amount: String(newAmount) }, { replace: true });
      setPhase('form');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar un nuevo pago');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('¿Cancelar el pago? No se realizará ningún cobro.')) return;
    setBusy(true);
    try {
      await api.post(`/booking-requests/${requestId}/bancard/cancel`);
      const r = await api.get(`/payments/bancard/status/${requestId}`);
      showResult('cancelled', r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cancelar el pago');
    } finally {
      setBusy(false);
    }
  };

  const propertyId = status?.metadata?.property_id;

  return (
    <div className="min-h-screen bg-[#FAFAF8] dark:bg-[#0C0A09] flex flex-col items-center justify-start pt-8 pb-12 px-4" data-testid="bancard-checkout-page">
      <div className="max-w-lg w-full mb-6">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(propertyId ? `/properties/${propertyId}` : -1)} className="text-[#57534E] hover:text-[#1C1917] dark:text-[#A8A29E] dark:hover:text-white transition-colors" data-testid="bancard-header-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-[#1C1917] dark:text-white" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>Pago seguro</h1>
        </div>
        <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg px-4 py-2">
          <ShieldCheck className="w-5 h-5 text-green-600 shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-400">Conexión segura con Bancard — tus datos están protegidos.</p>
        </div>
        {(env === 'staging' || status?.bancard_env === 'staging') && (
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2 mt-2" data-testid="bancard-staging-banner">
            <FlaskConical className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-400">Modo de pruebas (staging): no se realizan cobros reales.</p>
          </div>
        )}
        {phase === 'form' && amount && (
          <p className="text-sm text-[#57534E] dark:text-[#A8A29E] mt-3 text-center" data-testid="bancard-amount">
            Monto: <span className="font-bold text-[#1C1917] dark:text-white">{formatGs(amount)}</span>
          </p>
        )}
      </div>

      {phase === 'form' && (
        <>
          <BancardFrame processId={processId} env={env} loading={frameLoading}
            onReady={() => setFrameLoading(false)} onResult={handleFrameResult}
            onError={(msg) => showResult('error', status, msg)} />
          <button onClick={handleCancel} disabled={busy}
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#78716C] hover:text-[#991B1B] transition-colors disabled:opacity-60"
            data-testid="bancard-cancel-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Cancelar pago
          </button>
        </>
      )}

      {phase === 'verifying' && (
        <div className="max-w-md w-full bg-white dark:bg-[#1C1917] rounded-2xl shadow-xl p-8 text-center" data-testid="bancard-verifying">
          <Loader2 className="w-14 h-14 text-[#264639] mx-auto mb-5 animate-spin" />
          <h2 className="text-xl font-bold text-[#1C1917] dark:text-white mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>Verificando tu pago...</h2>
          <p className="text-sm text-[#57534E] dark:text-[#A8A29E]">Estamos confirmando la operación con Bancard. No cierres esta página.</p>
        </div>
      )}

      {phase === 'result' && (
        <BancardResult variant={variant} status={status} errorText={errorText} propertyId={propertyId}
          onRetry={handleRetry} onRecheck={verify} retrying={busy} />
      )}

      <p className="text-xs text-[#78716C] dark:text-[#888] mt-6 text-center max-w-md">
        Procesado por Bancard S.A. — Al aprobarse el pago, tu reserva se confirma automáticamente.
        ¿Dudas? Escribinos por WhatsApp al <a href="https://wa.me/595982188147" className="underline" data-testid="bancard-help-whatsapp">+595 982 188147</a>.
      </p>
    </div>
  );
}
